import { parseJsonArray } from '../lib/jsonColumn';

/**
 * Two kinds of occasional line item live in campaigns.campaign_expenses:
 *
 * - 'deduction'   a cost we carry that is subtracted from what we transfer to
 *                 the creator (e.g. "Débito NOS — Distribuição Publicidade").
 * - 'passthrough' a cost charged to the client and paid straight to the
 *                 creator. It is part of the campaign value but carries no
 *                 commission, so it is excluded from the commission base.
 *
 * Entries saved before pass-through existed have no kind and are deductions.
 */
export type CampaignExpenseKind = 'deduction' | 'passthrough';

export interface ParsedCampaignExpense {
  id?: string;
  description?: string;
  amount?: number;
  kind?: CampaignExpenseKind;
  /**
   * Payable tranche this expense was settled with: a deduction taken off that
   * payout, or a pass-through reimbursed in it. EXPENSE_SETTLED_ELSEWHERE when
   * it was settled outside the app's tranches.
   */
  settled_in?: string | null;
  settled_date?: string | null;
}

export const EXPENSE_SETTLED_ELSEWHERE = 'external';

export function parseCampaignExpenses(campaign: { campaign_expenses?: unknown }): ParsedCampaignExpense[] {
  return parseJsonArray<ParsedCampaignExpense>(campaign.campaign_expenses);
}

export function getExpenseKind(expense: ParsedCampaignExpense): CampaignExpenseKind {
  return expense.kind === 'passthrough' ? 'passthrough' : 'deduction';
}

/** Costs deducted from the creator payout (the Payments Ready email). */
export function getCampaignDeductionsTotal(campaign: { campaign_expenses?: unknown }): number {
  return parseCampaignExpenses(campaign)
    .filter((e) => getExpenseKind(e) === 'deduction')
    .reduce((sum, e) => sum + (e.amount || 0), 0);
}

export function isExpenseSettled(expense: ParsedCampaignExpense): boolean {
  return !!expense.settled_in;
}

/**
 * Deductions not yet taken off any payout. Payable tranches are what the
 * creator invoices; these come off the next transfer, once. A deduction
 * marked as settled was already withheld (Trade Republic 2026/89: the NOS
 * debit came off the August payout) and must not be subtracted again.
 */
export function getOutstandingDeductions(campaign: { campaign_expenses?: unknown }): ParsedCampaignExpense[] {
  return parseCampaignExpenses(campaign)
    .filter((e) => getExpenseKind(e) === 'deduction' && (e.amount || 0) > 0 && !isExpenseSettled(e));
}

export function getOutstandingDeductionsTotal(campaign: { campaign_expenses?: unknown }): number {
  return getOutstandingDeductions(campaign).reduce((sum, e) => sum + (e.amount || 0), 0);
}

/** Client-charged costs paid straight to the creator, never commissioned. */
export function getCampaignPassthroughTotal(campaign: { campaign_expenses?: unknown }): number {
  return parseCampaignExpenses(campaign)
    .filter((e) => getExpenseKind(e) === 'passthrough')
    .reduce((sum, e) => sum + (e.amount || 0), 0);
}

/**
 * After a payout: outstanding deductions withheld on it are settled with the
 * first tranche paid, and pending pass-through is settled with the tranche
 * that closes the campaign — so neither is withheld or itemised again.
 */
export function settleExpensesOnPayout<T extends ParsedCampaignExpense>(
  expenses: T[],
  { withheld, deductionTrancheId, closingTrancheId, date }: {
    withheld: number;
    deductionTrancheId?: string | null;
    closingTrancheId?: string | null;
    date: string;
  },
): T[] {
  let left = withheld;
  return expenses.map((e) => {
    const amount = e.amount || 0;
    if (isExpenseSettled(e) || amount <= 0) return e;
    if (getExpenseKind(e) === 'deduction') {
      if (!deductionTrancheId || amount > left + 0.005) return e;
      left -= amount;
      return { ...e, settled_in: deductionTrancheId, settled_date: date };
    }
    return closingTrancheId ? { ...e, settled_in: closingTrancheId, settled_date: date } : e;
  });
}
